/**
 * <SentShareRow> — single row in the Sent tab list view.
 *
 * `external-sharing` Phase 3 Session 3.3 (2026-05-09); extended in Phase 6
 * Session 6.1 with the `[Version graph]` action.
 *
 * Columns: Recipient | Status | Views | Created | Actions ([Copy link] +
 * [Version graph] + [✕] revoke).
 *
 * `[Version graph]` opens `<VersionGraphModal>` for the share's chain.
 * Rendered unconditionally — for solo shares the modal renders a single-node
 * empty-chain state ("This share hasn't been forked yet"), which is a useful
 * affordance in itself (the user gets explicit confirmation that no chain
 * exists rather than a hidden button).
 *
 * Highlight ring fires when `?highlight=<id>` matches; consumed by
 * `<SentTab>`'s scroll-into-view effect.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { formatNumber, formatRelativeTime } from '@/lib/format';
import { buildShareUrl, type SharedLinkResponse } from '@/lib/shares-api';
import { VersionGraphModal } from '@/components/sharing';
import { RevokeConfirmModal } from './RevokeConfirmModal';

const STATUS_LABELS: Record<SharedLinkResponse['status'], string> = {
  active: 'Active',
  // "Awaiting signup" replaces "Pending" (2026-05-10) — DB enum value stays
  // `pending`, only the user-facing label changes. The state means: link
  // is LIVE for anonymous viewing via /deal/<id>, but the recipient
  // hasn't claimed their account yet so authed features (Received inbox,
  // fork, share-back) stay gated for them.
  pending: 'Awaiting signup',
  revoked: 'Revoked',
};

const STATUS_TOOLTIPS: Record<SharedLinkResponse['status'], string> = {
  active: 'Recipient has signed in. They can open this deal from their inbox, fork it, or share back.',
  pending:
    'Link is live for anyone with the URL. The recipient hasn’t signed up yet, so they can’t fork or share back until they do.',
  revoked: 'You revoked this share. The link returns 404 to all viewers.',
};

/**
 * Grid-template-columns for the sent-share row. Exported so
 * `<SharesTableHeader>` can mount above the rows with the same template
 * — headers can't drift from cells.
 *
 * Every track is FIXED-width except the leading Recipient (2fr). Auto-sized
 * tracks would resolve to different widths in the header vs the row (header
 * actions are empty = 0px wide, row actions are buttons = ~200px wide), so
 * the `2fr` column would absorb different amounts of leftover space and
 * the columns would visually drift. With fixed widths, both grids
 * resolve to identical column tracks regardless of content.
 *
 * `minmax(0, 2fr)` lets the recipient column shrink below its intrinsic
 * content width — without `minmax(0, …)` a long email string would
 * stretch the column past 2fr and push the rest of the grid right.
 */
export const SENT_SHARE_ROW_GRID =
  'grid grid-cols-[minmax(0,2fr)_140px_72px_100px_260px]';

export interface SentShareRowProps {
  share: SharedLinkResponse;
  highlighted: boolean;
  onRevoke: (shareId: string) => void;
}

export function SentShareRow({ share, highlighted, onRevoke }: SentShareRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);

  const recipientLabel =
    share.recipientName && share.recipientName.trim().length > 0
      ? `${share.recipientName} (${share.recipientEmail})`
      : share.recipientEmail;

  const isRevoked = share.status === 'revoked';

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(share.id));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Non-secure context — silent fail, the input renders the URL anyway.
      console.warn('[SentShareRow] clipboard.writeText failed');
    }
  };

  const handleConfirmRevoke = (): void => {
    setConfirmOpen(false);
    onRevoke(share.id);
  };

  return (
    <>
    <div
      id={`share-row-${share.id}`}
      data-testid={`share-row-${share.id}`}
      data-status={share.status}
      className={clsx(
        SENT_SHARE_ROW_GRID,
        'items-center gap-4',
        'px-4 py-3 border border-panel-border bg-panel-bg rounded-[3px]',
        highlighted && 'ring-1 ring-accent border-accent/40',
        isRevoked && 'opacity-70',
      )}
    >
      <div className="min-w-0">
        <div className="text-sm font-mono text-text-bright truncate">{recipientLabel}</div>
        <div className="text-xs text-text-muted truncate">
          {share.conversationTitle || 'Untitled deal'}
        </div>
      </div>
      <StatusBadge status={STATUS_LABELS[share.status]} title={STATUS_TOOLTIPS[share.status]} />
      <span className="text-sm font-mono tabular-nums text-text">
        {formatNumber(share.viewCount)}
      </span>
      <span className="text-xs text-text-muted font-mono">
        {formatRelativeTime(share.createdAt)}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => void handleCopy()}
          data-testid={`share-row-${share.id}-copy`}
          className="!h-8 !py-0 !px-3 inline-flex items-center justify-center whitespace-nowrap text-xs"
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setGraphOpen(true)}
          data-testid={`share-row-${share.id}-version-graph`}
          className="!h-8 !py-0 !px-3 inline-flex items-center justify-center whitespace-nowrap text-xs"
          title="Open version graph"
        >
          Versions
        </Button>
        {!isRevoked && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            data-testid={`share-row-${share.id}-revoke`}
            aria-label={`Revoke share with ${share.recipientEmail}`}
            title="Revoke share"
            className={clsx(
              'flex items-center justify-center w-8 h-8 rounded-[2px] cursor-pointer',
              'border border-panel-border bg-input-bg text-text-muted',
              'hover:text-danger hover:border-danger/30 transition-colors',
            )}
          >
            ✕
          </button>
        )}
      </div>
    </div>
    <RevokeConfirmModal
      open={confirmOpen}
      recipientLabel={share.recipientEmail}
      onClose={() => setConfirmOpen(false)}
      onConfirm={handleConfirmRevoke}
    />
    <VersionGraphModal
      open={graphOpen}
      shareId={share.id}
      currentConversationId={share.conversationId}
      viewerKind="owner"
      onClose={() => setGraphOpen(false)}
    />
    </>
  );
}
